import { db } from "@/lib/firebase";
import { WordPressService } from "@/lib/wordpressService";
import {
  isWordPressConnected,
  PartialWordPressDomainIntegrationSchema,
  type PartialWordPressDomainIntegration,
  parseWordPressVersion,
  WordPressVersion,
  type WordPressSite,
  safeParseWordPressSite,
  type WordPressCategory,
  safeParseWordPressCategory,
} from "@/schemas";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface WordPressSlice {
  /* state */
  wpIntegration: PartialWordPressDomainIntegration | null;
  wpVersions: Record<string, WordPressVersion[]>;
  isLoadingWpVersions: Record<string, boolean>;
  wpVersionErrors: Record<string, string | null>;
  isLoadingWpIntegration: boolean;
  wpIntegrationError: string | null;
  wpActionLoading: Record<string, boolean>;
  wpActionError: string | null;
  wpSites: WordPressSite[];
  isLoadingWpSites: boolean;
  wpCategories: WordPressCategory[];
  isLoadingWpCategories: boolean;

  /* actions */
  subscribeToWpSites: (userId: string) => () => void;
  unsubscribeWpSites: () => void;
  subscribeToWpCategories: (orgId: string, domainId: string) => () => void;
  unsubscribeWpCategories: () => void;
  subscribeToWpIntegration: (orgId: string, domainId: string) => () => void;
  subscribeToPageVersions: (
    orgId: string,
    domainId: string,
    pageId: string,
  ) => () => void;
  unsubscribeWpIntegration: () => void;
  unsubscribeAllPageVersions: () => void;
  triggerSync: (domainId: string) => Promise<void>;
  createWpContent: (
    orgId: string,
    domainId: string,
    keyword: string,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ) => Promise<void>;
  editWpContent: (
    domainId: string,
    pageId: string,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ) => Promise<void>;
  saveManualPageVersion: (
    domainId: string,
    pageId: string,
    baseVersion: WordPressVersion,
    edits: Pick<
      WordPressVersion,
      "wp_title" | "wp_slug" | "wp_excerpt" | "wp_content"
    > & { wp_categories?: number[] },
  ) => Promise<void>;
  approveWpVersion: (
    domainId: string,
    pageId: string,
    versionId: string,
    wpType: "posts" | "pages",
  ) => Promise<void>;

  /* internals */
  _wpIntegrationUnsubscribe: (() => void) | null;
  _wpVersionsUnsubscribes: Map<string, () => void>;
  _wpSitesUnsubscribe: (() => void) | null;
  _wpCategoriesUnsubscribe: (() => void) | null;
}

export const createWordPressSlice: StateCreator<
  StoreState,
  [],
  [],
  WordPressSlice
> = (set, get) => ({
  /* initial state */
  wpIntegration: null,
  wpVersions: {},
  isLoadingWpVersions: {},
  wpVersionErrors: {},
  isLoadingWpIntegration: false,
  wpIntegrationError: null,
  wpActionLoading: {},
  wpActionError: null,
  wpSites: [],
  isLoadingWpSites: false,
  wpCategories: [],
  isLoadingWpCategories: false,
  _wpIntegrationUnsubscribe: null,
  _wpVersionsUnsubscribes: new Map(),
  _wpSitesUnsubscribe: null,
  _wpCategoriesUnsubscribe: null,

  /* actions */
  subscribeToWpSites: (userId) => {
    if (!userId) return () => {};

    get()._wpSitesUnsubscribe?.();
    set({ isLoadingWpSites: true });

    const sitesRef = collection(
      db,
      `users/${userId}/integrations/wordpress/sites`,
    );

    const unsubscribe = onSnapshot(
      sitesRef,
      (snap) => {
        const sites: WordPressSite[] = [];
        snap.forEach((docSnap) => {
          const result = safeParseWordPressSite(docSnap.data());
          if (result.success) {
            sites.push(result.data);
          } else {
            console.error(`WP site parse error for ${docSnap.id}:`, result.error);
          }
        });
        set({ wpSites: sites, isLoadingWpSites: false });
      },
      (err) => {
        console.error("WP sites subscription error:", err);
        set({ isLoadingWpSites: false });
      },
    );

    set({ _wpSitesUnsubscribe: unsubscribe });
    return unsubscribe;
  },

  unsubscribeWpSites: () => {
    get()._wpSitesUnsubscribe?.();
    set({ _wpSitesUnsubscribe: null, wpSites: [] });
  },

  subscribeToWpCategories: (orgId, domainId) => {
    if (!orgId || !domainId) return () => {};

    get()._wpCategoriesUnsubscribe?.();
    set({ isLoadingWpCategories: true });

    const categoriesRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/wordpress_categories`,
    );

    const unsubscribe = onSnapshot(
      categoriesRef,
      (snap) => {
        const categories: WordPressCategory[] = [];
        snap.forEach((docSnap) => {
          const result = safeParseWordPressCategory(docSnap.data());
          if (result.success) {
            categories.push(result.data);
          } else {
            console.error(
              `WP category parse error for ${docSnap.id}:`,
              result.error,
            );
          }
        });
        categories.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        );
        set({ wpCategories: categories, isLoadingWpCategories: false });
      },
      (err) => {
        console.error("WP categories subscription error:", err);
        set({ isLoadingWpCategories: false });
      },
    );

    set({ _wpCategoriesUnsubscribe: unsubscribe });
    return unsubscribe;
  },

  unsubscribeWpCategories: () => {
    get()._wpCategoriesUnsubscribe?.();
    set({ _wpCategoriesUnsubscribe: null, wpCategories: [] });
  },

  subscribeToWpIntegration: (orgId, domainId) => {
    if (!orgId || !domainId) return () => {};

    get()._wpIntegrationUnsubscribe?.();
    set({ isLoadingWpIntegration: true, wpIntegrationError: null });

    const integrationRef = doc(
      db,
      `organizations/${orgId}/domains/${domainId}/integrations/wordpress`,
    );

    const unsubscribe = onSnapshot(
      integrationRef,
      (snap) => {
        if (!snap.exists()) {
          set({ wpIntegration: null, isLoadingWpIntegration: false });
          return;
        }
        // Parse tolerantly: `core` can create this doc from its sync error
        // path with only {site_id, sync_status, last_sync_attempt_at,
        // error_message}, and dropping it made a broken WordPress connection
        // invisible in the UI. Keep the doc so the error surfaces; callers ask
        // `isWordPressConnected` before treating it as a live integration.
        const result = PartialWordPressDomainIntegrationSchema.safeParse(
          snap.data(),
        );
        if (!result.success) {
          console.error(
            "WP integration parse error:",
            result.error,
            snap.data(),
          );
          set({
            wpIntegration: null,
            wpIntegrationError: "Failed to parse WP integration data",
            isLoadingWpIntegration: false,
          });
          return;
        }
        set({
          wpIntegration: result.data,
          wpIntegrationError: isWordPressConnected(result.data)
            ? null
            : (result.data.error_message ??
              "WordPress integration is incomplete — reconnect the site."),
          isLoadingWpIntegration: false,
        });
      },
      (err) => {
        console.error("WP integration subscription error:", err);
        set({
          wpIntegrationError: err.message,
          isLoadingWpIntegration: false,
        });
      },
    );

    set({ _wpIntegrationUnsubscribe: unsubscribe });
    return unsubscribe;
  },

  subscribeToPageVersions: (orgId, domainId, pageId) => {
    if (!orgId || !domainId || !pageId) return () => {};

    const existingUnsubs = get()._wpVersionsUnsubscribes;
    const existingUnsubscribe = existingUnsubs.get(pageId);
    if (existingUnsubscribe) {
      return existingUnsubscribe;
    }

    set((state) => ({
      isLoadingWpVersions: {
        ...state.isLoadingWpVersions,
        [pageId]: !state.wpVersions[pageId],
      },
      wpVersionErrors: {
        ...state.wpVersionErrors,
        [pageId]: null,
      },
    }));

    const versionsRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/pages/${pageId}/versions`,
    );
    const q = query(versionsRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const versions: WordPressVersion[] = [];
        snap.forEach((docSnap) => {
          try {
            versions.push(parseWordPressVersion({ id: docSnap.id, ...docSnap.data() }));
          } catch (err) {
            console.error(`Version parse error for ${docSnap.id}:`, err);
          }
        });
        set((state) => ({
          wpVersions: { ...state.wpVersions, [pageId]: versions },
          isLoadingWpVersions: {
            ...state.isLoadingWpVersions,
            [pageId]: false,
          },
        }));
      },
      (err) => {
        console.error(`Versions subscription error for page ${pageId}:`, err);
        set((state) => ({
          isLoadingWpVersions: {
            ...state.isLoadingWpVersions,
            [pageId]: false,
          },
          wpVersionErrors: {
            ...state.wpVersionErrors,
            [pageId]: err.message,
          },
        }));
      },
    );

    const newMap = new Map(existingUnsubs);
    newMap.set(pageId, unsubscribe);
    set({ _wpVersionsUnsubscribes: newMap });
    return unsubscribe;
  },

  unsubscribeWpIntegration: () => {
    get()._wpIntegrationUnsubscribe?.();
    set({ _wpIntegrationUnsubscribe: null, wpIntegration: null });
  },

  unsubscribeAllPageVersions: () => {
    get()._wpVersionsUnsubscribes.forEach((unsub) => unsub());
    set({
      _wpVersionsUnsubscribes: new Map(),
      wpVersions: {},
      isLoadingWpVersions: {},
      wpVersionErrors: {},
    });
  },

  triggerSync: async (domainId) => {
    const wpIntegration = get().wpIntegration;
    if (!wpIntegration?.site_id) {
      set({ wpActionError: "No WP site connected" });
      return;
    }
    set((state) => ({
      wpActionLoading: { ...state.wpActionLoading, sync: true },
      wpActionError: null,
    }));
    try {
      // A sync records its failures rather than raising them, so core answers
      // 200 with `status: "error"` and the reason in `message`. Only checking
      // whether the call threw would report a sync that did nothing as a
      // success -- which is exactly the bug this pairs with on the core side.
      const resp = await WordPressService.syncWordPress(
        domainId,
        wpIntegration.site_id,
      );
      if (resp.status === "error") {
        set({ wpActionError: resp.message });
      }
    } catch (err) {
      set({ wpActionError: err instanceof Error ? err.message : "Sync failed" });
    } finally {
      set((state) => ({
        wpActionLoading: { ...state.wpActionLoading, sync: false },
      }));
    }
  },

  createWpContent: async (orgId, domainId, keyword, wpType, customInstructions) => {
    set((state) => ({
      wpActionLoading: { ...state.wpActionLoading, create: true },
      wpActionError: null,
    }));
    try {
      const resp = wpType === "posts"
        ? await WordPressService.createPost(domainId, keyword, customInstructions)
        : await WordPressService.createPage(domainId, keyword, customInstructions);

      if (resp.page_id) {
        get().subscribeToPageVersions(orgId, domainId, resp.page_id);
      }
    } catch (err) {
      set({ wpActionError: err instanceof Error ? err.message : "Create failed" });
    } finally {
      set((state) => ({
        wpActionLoading: { ...state.wpActionLoading, create: false },
      }));
    }
  },

  editWpContent: async (domainId, pageId, wpType, customInstructions) => {
    set((state) => ({
      wpActionLoading: { ...state.wpActionLoading, [pageId]: true },
      wpActionError: null,
    }));
    try {
      if (wpType === "posts") {
        await WordPressService.editPost(
          domainId,
          pageId,
          customInstructions,
        );
      } else {
        await WordPressService.editPage(
          domainId,
          pageId,
          customInstructions,
        );
      }
    } catch (err) {
      set({ wpActionError: err instanceof Error ? err.message : "Edit failed" });
    } finally {
      set((state) => ({
        wpActionLoading: { ...state.wpActionLoading, [pageId]: false },
      }));
    }
  },

  saveManualPageVersion: async (
    domainId,
    pageId,
    baseVersion,
    edits,
  ) => {
    const wpType: "posts" | "pages" =
      baseVersion.wp_type === "post" ? "posts" : "pages";
    await WordPressService.saveManualVersion(domainId, pageId, wpType, {
      wp_title: edits.wp_title,
      wp_slug: edits.wp_slug,
      wp_excerpt: edits.wp_excerpt,
      wp_content: edits.wp_content,
      wp_categories: edits.wp_categories,
    });
  },

  approveWpVersion: async (domainId, pageId, versionId, wpType) => {
    set((state) => ({
      wpActionLoading: { ...state.wpActionLoading, [versionId]: true },
      wpActionError: null,
    }));
    try {
      await WordPressService.approveVersion(domainId, pageId, versionId, wpType);
    } catch (err) {
      set({ wpActionError: err instanceof Error ? err.message : "Approve failed" });
    } finally {
      set((state) => ({
        wpActionLoading: { ...state.wpActionLoading, [versionId]: false },
      }));
    }
  },
});
